<script setup lang="ts">
  import type { ObjectSchema } from 'yup'
  import { notify } from 'notiwind'
  import { Form } from 'vee-validate'
  import * as yup from 'yup'

  const props = withDefaults(
    defineProps<{
      footer?: boolean
    }>(),
    {
      footer: false
    }
  )

  const resolvedSubmitLabel = computed(() => (props.footer ? "S'abonner" : "S'abonner ici ►"))

  const titleClasses = computed(() =>
    props.footer ? ['font-special text-base font-bold uppercase text-white'] : ['font-special text-3xl font-bold text-white sm:text-4xl']
  )

  const inputClasses = computed(() =>
    props.footer
      ? ['!rounded border-transparent !px-3 !py-1.5 !text-sm !outline-none !ring-0']
      : ['!rounded border-transparent !px-4 !py-3 !outline-none !ring-0']
  )

  const fieldInputClass = (error?: string) =>
    [inputClasses.value, error ? '!border-red-500 !bg-red-100 !text-red-500' : '!text-tarawera-900'].filter(Boolean).join(' ')

  const buttonClasses = computed(() =>
    props.footer
      ? [
          '!rounded bg-[#63D0D9] font-special font-bold text-tarawera-900 transition-colors hover:bg-[#52cdd7] disabled:opacity-60 self-start !px-3 !py-1 text-sm'
        ]
      : ['!rounded bg-[#63D0D9] font-special font-bold text-tarawera-900 transition-colors hover:bg-[#52cdd7] disabled:opacity-60 w-full !px-7 !py-3']
  )

  type NewsletterValues = {
    email: string
    surname: string
    first_name: string
  }

  const submitting = ref(false)

  const schema = yup.object({
    surname: yup.string().required('${label} est requis').label('Nom'),
    first_name: yup.string().required('${label} est requis').label('Prénom'),
    email: yup.string().required('${label} est requis').email('${label} est invalide').label('Email')
  }) as ObjectSchema<NewsletterValues>

  async function onSubmit(values: NewsletterValues, actions: { resetForm: () => void }) {
    if (submitting.value) return
    submitting.value = true
    try {
      const body = new URLSearchParams()
      for (const [key, value] of Object.entries(values)) {
        if (value != null && value !== '') body.append(key, value)
      }
      await fetch('https://systeme.io/embedded/40061264/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        redirect: 'manual',
        body
      })
      actions.resetForm()
      notify({
        group: 'main',
        type: 'success',
        title: 'Inscription validée',
        text: `Vous recevrez une confirmation par email d'ici quelques instants.`
      })
    } catch {
      notify({ group: 'main', type: 'danger', title: 'Erreur', text: 'Une erreur est survenue, veuillez réessayer.' })
    }
    submitting.value = false
  }
</script>

<template>
  <div :class="footer ? 'flex w-full flex-col' : 'mx-auto flex w-full max-w-screen-xl flex-col items-center justify-center gap-10 md:flex-row'">
    <div :class="footer ? '' : 'flex flex-col text-center md:text-left'">
      <h2 :class="titleClasses">
        <template v-if="footer">La newsletter des employeurs publics</template>
        <template v-else>La newsletter des<br />employeurs publics</template>
      </h2>
      <p :class="footer ? 'mt-2 text-sm text-white/80' : 'mt-4 max-w-xs text-white/80'">
        Votre rendez-vous mensuel pour décrypter les bonnes pratiques RH du secteur public
      </p>
    </div>

    <Form
      v-slot="{ errors }"
      :class="footer ? 'mt-3 flex flex-col gap-2' : 'flex w-full max-w-sm flex-col gap-3'"
      :validation-schema="schema"
      @submit="onSubmit"
    >
      <div class="flex gap-2" :class="footer ? 'flex-row' : 'flex-col gap-3'">
        <FormField class="flex-1" :input-class="fieldInputClass(errors.surname)" name="surname" placeholder="Nom" required />
        <FormField class="flex-1" :input-class="fieldInputClass(errors.first_name)" name="first_name" placeholder="Prénom" required />
      </div>
      <FormField :input-class="fieldInputClass(errors.email)" name="email" placeholder="Email pro" type="email" required />
      <Button :class="buttonClasses" type="submit" :disabled="submitting">
        {{ submitting ? 'Inscription...' : resolvedSubmitLabel }}
      </Button>
    </Form>
  </div>
</template>
